'use client';

import { useState, useRef, useEffect, useCallback, useId, type KeyboardEvent } from 'react';
import { X, ArrowClockwise } from '@phosphor-icons/react';
import { MapPinIcon, SearchIcon, SpinnerIcon } from '@/components/icons';
import { isAxiosError } from 'axios';
import { cn } from '@/lib/utils';
import { useAddressStore } from '@/stores/address-store';
import { deliveryApi, type AddressSuggestion } from '@/lib/api/delivery';
import './address-input.css';

interface AddressInputProps {
  value?: string;
  onChange?: (value: string) => void;
  onSelect?: (suggestion: AddressSuggestion) => void;
  placeholder?: string;
  className?: string;
  autoFocus?: boolean;
  id?: string;
  label?: string;
}

type SearchState = {
  query: string;
  cityId: number | undefined;
  status: 'loading' | 'success' | 'error';
  suggestions: AddressSuggestion[];
};

/** Один запрос продолжает другой: человек дописывает или стирает тот же адрес. */
function continuesQuery(previous: string, next: string) {
  const a = previous.trim().toLowerCase();
  const b = next.trim().toLowerCase();
  return a.startsWith(b) || b.startsWith(a);
}

// Столько после выбора адреса соседние кнопки не реагируют на нажатия (двойной тап).
const JUST_SELECTED_MS = 400;

export function AddressInput({
  value: controlledValue,
  onChange,
  onSelect,
  placeholder = 'Улица и номер дома',
  className,
  autoFocus = false,
  id,
  label = 'Адрес доставки',
}: AddressInputProps) {
  const generatedId = useId();
  const inputId = id ?? `address-search-${generatedId}`;
  const listId = `${inputId}-list`;
  const statusId = `${inputId}-status`;
  const [internalValue, setInternalValue] = useState('');
  const [query, setQuery] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [search, setSearch] = useState<SearchState | null>(null);
  // Последний удачный ответ: его показываем, пока ищется уточнённый запрос.
  const [settled, setSettled] = useState<SearchState | null>(null);
  const [justSelected, setJustSelected] = useState(false);
  const [retry, setRetry] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const requestRef = useRef(0);
  const selectionRef = useRef(0);
  // Палец или мышь на подсказке: поле может потерять фокус раньше, чем придёт click.
  const interactingRef = useRef(false);
  const justSelectedTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const justSelectedRef = useRef(false);
  // Кнопка вне поля, на которую сейчас нажимают: список закроет её собственный click.
  const pressedControlRef = useRef<Element | null>(null);
  const selectedCity = useAddressStore((state) => state.selectedCity);
  const isCheckingAddress = useAddressStore((state) => state.isLoading);
  const clearSuggestions = useAddressStore((state) => state.clearSuggestions);
  const selectSuggestion = useAddressStore((state) => state.selectSuggestion);
  const value = controlledValue ?? internalValue;
  const cityId = selectedCity?.id;
  const cityName = selectedCity?.name;

  const setValue = useCallback(
    (next: string) => {
      setInternalValue(next);
      onChange?.(next);
    },
    [onChange]
  );

  // Results belong to the exact text and city that requested them. The shared
  // store autocomplete has no request identity, so keep transient search local.
  useEffect(() => {
    const sequence = requestRef;
    const request = ++sequence.current;
    if (query === null || query !== value || query.trim().length < 3) return;
    const timer = setTimeout(async () => {
      if (request !== sequence.current) return;
      setSearch({ query, cityId, status: 'loading', suggestions: [] });
      try {
        const result = await deliveryApi.autocomplete(query.trim(), cityName);
        if (request !== sequence.current) return;
        const found: SearchState = {
          query,
          cityId,
          status: 'success',
          suggestions: result.suggestions ?? [],
        };
        setSearch(found);
        setSettled(found);
        // Строки сменились только сейчас: пока шёл поиск, стрелки ходили по прежнему списку.
        setActiveIndex(-1);
      } catch (error) {
        if (request !== sequence.current) return;
        setSearch({ query, cityId, status: 'error', suggestions: [] });
        setActiveIndex(-1);
        // Preserve the existing autocomplete failure event, without treating
        // an unavailable search service as an address outside delivery zones.
        void import('@/lib/source-tracker')
          .then(({ captureEvent }) => {
            captureEvent('address_check_failed', {
              reason: 'suggest_failed',
              http_status: isAxiosError(error) ? (error.response?.status ?? null) : null,
              query,
              has_prev_address: !!useAddressStore.getState().selectedAddress,
            });
          })
          .catch(() => {});
      }
    }, 300);
    return () => {
      clearTimeout(timer);
      if (sequence.current === request) ++sequence.current;
    };
  }, [query, value, cityId, cityName, retry]);

  useEffect(
    () => () => {
      ++requestRef.current;
      ++selectionRef.current;
      clearTimeout(justSelectedTimer.current);
    },
    []
  );

  useEffect(() => {
    ++selectionRef.current;
  }, [cityId]);

  const canSearch = query === value && value.trim().length >= 3;
  const currentSearch = search?.query === value && search.cityId === cityId ? search : null;
  const isSearching = canSearch && (!currentSearch || currentSearch.status === 'loading');
  // Пока ищется уточнённый запрос, прошлые подсказки остаются на месте. Иначе список на
  // каждую букву схлопывался в «Ищем адрес…», и кнопка под ним прыгала под палец.
  const shown =
    currentSearch?.status === 'success'
      ? currentSearch
      : isSearching && settled && settled.cityId === cityId && continuesQuery(settled.query, value)
        ? settled
        : null;
  // Улица, которую человек только что выбрал, в списке уже не нужна — остаются её дома.
  // Сверяем и с запросом, которому принадлежит список: пока человек дописывает номер
  // дома, показан ещё прошлый ответ, и улица не должна в него вернуться.
  const suggestions =
    canSearch && shown
      ? shown.suggestions.filter(
          (suggestion) =>
            suggestion.data.house ||
            (suggestion.value !== value.trim() && suggestion.value !== shown.query.trim())
        )
      : [];
  const hasError = canSearch && currentSearch?.status === 'error';
  const showPopup = isOpen && canSearch;
  const showList = showPopup && suggestions.length > 0;
  const activeOption =
    showList && activeIndex >= 0 && activeIndex < suggestions.length ? activeIndex : -1;

  useEffect(() => {
    if (activeOption >= 0)
      listRef.current?.children[activeOption]?.scrollIntoView({ block: 'nearest' });
  }, [activeOption]);

  const handleChange = (next: string) => {
    ++requestRef.current;
    ++selectionRef.current;
    setValue(next);
    setQuery(next);
    setActiveIndex(-1);
    setIsOpen(true);
    clearSuggestions();
  };

  const handleSelect = async (suggestion: AddressSuggestion) => {
    interactingRef.current = false;
    // Без номера дома адрес не проверить — оставляем человека в поле дописать дом.
    const needsHouse = !suggestion.data.house;
    const nextValue = needsHouse ? `${suggestion.value} ` : suggestion.value;
    // Сначала снимаем фокус: клавиатура телефона завершает незаконченное слово
    // (IME composition) ДО подстановки адреса. Пока фокус оставался в поле, часть
    // клавиатур возвращала набранный текст поверх выбранной подсказки — и выбор
    // «не срабатывал» (жалобы 10.2026: у кого-то выбирается, у кого-то нет).
    inputRef.current?.blur();
    const selection = ++selectionRef.current;
    ++requestRef.current;
    setValue(nextValue);
    // Для улицы список не прячем: тут же ищем её дома, чтобы осталось выбрать номер.
    setQuery(needsHouse ? nextValue : null);
    // Прошлые подсказки под пальцем не оставляем: повторное касание выбрало бы соседнюю улицу.
    setSettled(null);
    setActiveIndex(-1);
    setIsOpen(needsHouse);
    clearSuggestions();
    // Список под пальцем закрылся или сжался до «Ищем адрес…» — второе касание
    // двойного тапа не должно достаться кнопке, которая оказалась на его месте.
    justSelectedRef.current = true;
    setJustSelected(true);
    clearTimeout(justSelectedTimer.current);
    justSelectedTimer.current = setTimeout(() => {
      justSelectedRef.current = false;
      setJustSelected(false);
    }, JUST_SELECTED_MS);
    if (needsHouse) {
      // В том же жесте, иначе iOS не вернёт клавиатуру; курсор — в конец, после рендера.
      inputRef.current?.focus();
      setTimeout(() => {
        const field = inputRef.current;
        if (field && document.activeElement === field)
          field.setSelectionRange(field.value.length, field.value.length);
      }, 0);
    }
    // selectSuggestion owns the zone check and coordinate persistence. Keep
    // the existing callback order; a parent must not launch a second check.
    await selectSuggestion(suggestion);
    if (
      selection === selectionRef.current &&
      inputRef.current?.value === nextValue &&
      useAddressStore.getState().selectedCity?.id === cityId
    )
      onSelect?.(suggestion);
  };

  // Список закрывается нажатием вне поля, а не только потерей фокуса: после тапа по
  // подсказке фокуса в поле уже может не быть, и событие blur больше не придёт.
  useEffect(() => {
    if (!isOpen) return;
    const closeOutside = (event: PointerEvent) => {
      const target = event.target instanceof Element ? event.target : null;
      if (target && containerRef.current?.contains(target)) return;
      // Это второе касание двойного тапа по подсказке: список с домами улицы оставляем.
      if (justSelectedRef.current) return;
      interactingRef.current = false;
      const control = target?.closest('button, a, select') ?? null;
      pressedControlRef.current = control;
      if (!control) {
        setIsOpen(false);
        return;
      }
      // Нажали кнопку — например «Выбрать на карте» прямо под списком. Закрой список
      // сейчас, она уедет из-под пальца раньше, чем придёт click, и нажатие пропадёт.
      // Поэтому список закрывает сам click по этой кнопке.
      control.addEventListener(
        'click',
        () => {
          if (pressedControlRef.current === control) pressedControlRef.current = null;
          setIsOpen(false);
        },
        { once: true }
      );
    };
    document.addEventListener('pointerdown', closeOutside);
    return () => {
      document.removeEventListener('pointerdown', closeOutside);
      pressedControlRef.current = null;
    };
  }, [isOpen]);

  const handleClear = () => {
    ++requestRef.current;
    ++selectionRef.current;
    setValue('');
    setQuery(null);
    setSearch(null);
    setSettled(null);
    setActiveIndex(-1);
    setIsOpen(false);
    clearSuggestions();
    inputRef.current?.focus();
  };

  const handleRetry = () => {
    setSearch({ query: value, cityId, status: 'loading', suggestions: [] });
    setActiveIndex(-1);
    setIsOpen(true);
    setRetry((previous) => previous + 1);
    inputRef.current?.focus();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.nativeEvent.isComposing) return;
    if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && canSearch) {
      event.preventDefault();
      setIsOpen(true);
      if (suggestions.length) {
        const next =
          !showList || activeOption === -1
            ? event.key === 'ArrowDown'
              ? 0
              : suggestions.length - 1
            : (activeOption + (event.key === 'ArrowDown' ? 1 : -1) + suggestions.length) %
              suggestions.length;
        setActiveIndex(next);
      }
    } else if (event.key === 'Enter' && activeOption >= 0) {
      event.preventDefault();
      void handleSelect(suggestions[activeOption]);
    } else if (event.key === 'Escape' && showPopup) {
      event.preventDefault();
      event.stopPropagation();
      setIsOpen(false);
      setActiveIndex(-1);
    }
  };

  const statusMessage = showPopup
    ? isSearching
      ? 'Ищем адрес…'
      : hasError
        ? 'Поиск адреса временно недоступен.'
        : suggestions.length
          ? `Найдено адресов: ${suggestions.length}. Для выбора используйте стрелки и Enter.`
          : 'Адрес не найден. Уточните улицу и номер дома.'
    : isCheckingAddress
      ? 'Проверяем адрес…'
      : '';

  return (
    <div
      ref={containerRef}
      className={cn('address-search', className)}
      data-just-selected={justSelected ? '' : undefined}
      onBlur={(event) => {
        // Идёт тап по подсказке: список обязан дожить до click, иначе выбор теряется.
        if (interactingRef.current) return;
        // Фокус ушёл «в никуда» (клавиатуру убрали кнопкой «Готово») — список остаётся,
        // его закрывает нажатие вне поля. По blur закрываем только переход на другой
        // элемент (Tab).
        const next = event.relatedTarget as Node | null;
        if (!next || event.currentTarget.contains(next)) return;
        // Фокус забрала кнопка, которую сейчас нажимают, — список закроет её click.
        if (pressedControlRef.current?.contains(next)) return;
        setIsOpen(false);
      }}
    >
      <label className="address-search-label" htmlFor={inputId}>
        {label}
      </label>
      <div className="address-search-field">
        {isSearching || isCheckingAddress ? (
          <SpinnerIcon
            className="address-search-icon address-search-spinner"
            size={21}
            aria-hidden="true"
          />
        ) : (
          <SearchIcon className="address-search-icon" size={21} aria-hidden="true" />
        )}
        <input
          ref={inputRef}
          id={inputId}
          type="text"
          role="combobox"
          aria-autocomplete="list"
          aria-haspopup="listbox"
          aria-expanded={showPopup}
          aria-controls={showPopup ? listId : undefined}
          aria-activedescendant={activeOption >= 0 ? `${listId}-${activeOption}` : undefined}
          aria-describedby={statusId}
          value={value}
          onChange={(event) => handleChange(event.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            interactingRef.current = false;
            setIsOpen(true);
          }}
          placeholder={placeholder}
          autoFocus={autoFocus}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
        />
        {value && (
          <button
            className="address-search-clear"
            type="button"
            aria-label="Очистить адрес"
            onClick={handleClear}
          >
            <X size={19} aria-hidden="true" />
          </button>
        )}
      </div>
      <span
        className="address-search-sr"
        id={statusId}
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {statusMessage}
      </span>
      {showPopup && (
        <div
          className="address-search-popup"
          // Без preventDefault: поле теряет фокус естественно, как у обычной кнопки, а
          // список держит флаг interactingRef. Отмена pointerdown работала не во всех
          // мобильных браузерах (WebKit bug 313688) и удерживала в поле набор клавиатуры.
          onPointerDown={() => {
            interactingRef.current = true;
          }}
          onPointerCancel={() => {
            interactingRef.current = false;
          }}
        >
          <ul
            className="address-search-list"
            id={listId}
            role="listbox"
            aria-label="Подсказки адреса"
            aria-busy={isSearching}
            ref={listRef}
          >
            {suggestions.map((suggestion, index) => (
              <li
                key={`${suggestion.unrestricted_value || suggestion.value}-${index}`}
                className="address-search-option"
                id={`${listId}-${index}`}
                role="option"
                aria-selected={activeOption === index}
                onClick={() => {
                  void handleSelect(suggestion);
                }}
                onPointerMove={(event) => {
                  if (event.pointerType === 'mouse') setActiveIndex(index);
                }}
              >
                <MapPinIcon size={20} aria-hidden="true" />
                <div>
                  <span>{suggestion.value}</span>
                  {!suggestion.data.house ? (
                    <small>Уточните номер дома</small>
                  ) : (
                    suggestion.data.city &&
                    suggestion.data.city !== selectedCity?.name && (
                      <small>{suggestion.data.city}</small>
                    )
                  )}
                </div>
              </li>
            ))}
          </ul>
          {isSearching && suggestions.length === 0 && (
            <div className="address-search-message">
              <span>Ищем адрес…</span>
            </div>
          )}
          {hasError && (
            <div className="address-search-message address-search-error">
              <strong>Поиск временно недоступен</strong>
              <p>Повторите попытку или укажите дом на карте.</p>
              <button type="button" onClick={handleRetry}>
                <ArrowClockwise size={18} aria-hidden="true" />
                Повторить поиск
              </button>
            </div>
          )}
          {!isSearching && !hasError && suggestions.length === 0 && (
            <div className="address-search-message">
              <strong>Адрес не найден</strong>
              <p>Попробуйте уточнить улицу и номер дома.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
